import { useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { Users, LogOut, RefreshCw, Undo2, FileText, Info } from "lucide-react";

import { membershipState, groupActions, joinMessage, MESSAGE_MAX, isGroupPaused, groupRules } from "@/lib/groupMembership";
import { groupName, groupHref, policyText, membersCountText, joinPolicy, legacyTab } from "@/lib/v3Groups";
import { text } from "@/lib/memberDerive";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, ConfirmDialog, ErrorNote } from "../components/kit";
import { V3Title, V3Columns, V3Actions, StatusBanner, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { V3TextArea, V3_GHOST } from "../components/v3app";
import { useGroup, useMembership, GroupFrame, GroupAside, GroupNoAccess } from "../components/v3groups";

const PAUSED = "Dieser Kreis ist vorübergehend pausiert.";
const PAUSED_TEXT = "Beitreten sowie neue Beiträge, Kommentare und Nachrichten im Kreis sind gerade nicht möglich. Bestätigte Mitglieder können weiterhin lesen. Austreten und offene Anfragen zurückziehen geht weiterhin; eine bestehende Mitgliedschaft oder Anfrage bleibt unverändert.";

function Notes({ paused, note, unknown }) {
  return (<>
    {paused && <div role="status" data-testid="group-paused-note"><StatusBanner tone="notice" title={PAUSED} text={PAUSED_TEXT} /></div>}
    {note && <div role="status" data-testid="group-membership-note"><StatusBanner tone="notice" icon={Info} title={unknown ? "Status nicht bestätigt" : "Hinweis"} text={note} /></div>}
  </>);
}

// M15: own circle; internal lists that the Core does not deliver yet stay marked as not available (D-51, D-52).
function Room({ g, onLeaveHref }) {
  return (<>
    <DetailList>
      <DetailRow label="Kreis" value={groupName(g)} testid="group-room-name" />
      <DetailRow label="Mitgliedschaft" value="Du bist aufgenommen" testid="group-detail-status" />
      <DetailRow label="Mitglieder" value={membersCountText(g)} to={groupHref(g, "/mitglieder")} testid="group-room-members" />
      <DetailRow label="Nächstes Treffen" value="Noch nicht verfügbar" testid="group-room-meeting" />
      <DetailRow label="Verantwortlich" value="Nicht angegeben" testid="group-room-responsible" />
    </DetailList>
    <V3Actions>
      <Link to="/nachrichten" data-testid="group-chat-link" className={V3_PRIMARY}>Kreisgespräch öffnen</Link>
      <Link to="/kreise" data-testid="group-back" className={V3_SECONDARY}>Zurück</Link>
    </V3Actions>
    <div className="mt-[16px] flex flex-wrap gap-2">
      <Link to={`/community?bereich=kreis&kreis=${encodeURIComponent(g.id)}`} data-testid="group-posts-link" className={V3_GHOST}><FileText className="h-4 w-4" aria-hidden="true" />Beiträge des Kreises</Link>
      <Link to={groupHref(g, "/mitglieder")} data-testid="group-members-link" className={V3_GHOST}><Users className="h-4 w-4" aria-hidden="true" />Mitglieder</Link>
      <Link to={onLeaveHref} data-testid="group-leave" className={V3_GHOST}><LogOut className="h-4 w-4" aria-hidden="true" />Kreis verlassen</Link>
    </div>
  </>);
}

// M69: public preview before joining; the join request is an explicit, separate step.
function Preview({ g, state, can, busy, onJoin, onRecheck, paused }) {
  return (<>
    <DetailList>
      <DetailRow label="Kreis" value={groupName(g)} testid="group-preview-name" />
      <DetailRow label="Beschreibung" value={text(g.description).trim() || "Keine Beschreibung angegeben"} testid="group-preview-description" />
      <DetailRow label="Aufnahme" value={policyText(g)} testid="group-preview-policy" />
      {state !== "not_member" && <DetailRow label="Status" value={state === "join_declined" ? "Deine Beitrittsanfrage wurde abgelehnt." : "Status unbekannt"} testid="group-detail-status" />}
      <DetailRow label="Interne Beiträge und Chats" value="Noch nicht sichtbar" testid="group-preview-internal" />
    </DetailList>
    <V3Actions>
      {can.join && <button type="button" onClick={onJoin} data-testid="group-join" className={V3_PRIMARY}>Beitritt anfragen</button>}
      {paused && state === "not_member" && <button type="button" disabled data-testid="group-join-paused" className={V3_PRIMARY}>Beitritt gerade nicht möglich</button>}
      {state === "unknown" && <button type="button" onClick={onRecheck} disabled={busy} data-testid="group-recheck" className={V3_PRIMARY}><RefreshCw className="h-4 w-4" aria-hidden="true" />Status erneut prüfen</button>}
      <Link to="/kreise" data-testid="group-back" className={V3_SECONDARY}>Zurück</Link>
    </V3Actions>
  </>);
}

// M71: request sent, decision open; reading the rules is not recorded as consent yet (D-54).
function Pending({ g, can, busy, onWithdraw, onRecheck }) {
  const r = groupRules(g);
  return (<>
    <StatusBanner tone="sand" icon={Info} title="Aufnahme offen" text={groupName(g)} testid="group-pending-banner" />
    <DetailList>
      <DetailRow label="Kreis" value={groupName(g)} testid="group-pending-name" />
      <DetailRow label="Status" value="Aufnahme ausstehend" testid="group-detail-status" />
      <DetailRow label="Regeln" value={r ? `Fassung ${r.version} zum Lesen · Zustimmung wird noch nicht gespeichert` : "Keine freigegebene Fassung"} testid="group-pending-rules" />
      <DetailRow label="Interne Inhalte" value="Bis zur Aufnahme nicht verfügbar" testid="group-pending-internal" />
    </DetailList>
    <V3Actions>
      <Link to={groupHref(g, "/regeln")} data-testid="group-pending-details" className={V3_PRIMARY}>Details öffnen</Link>
      <Link to="/kreise" data-testid="group-back" className={V3_SECONDARY}>Zurück</Link>
    </V3Actions>
    <div className="mt-[16px] flex flex-wrap gap-2">
      <button type="button" onClick={onRecheck} disabled={busy} data-testid="group-recheck" className={V3_GHOST}><RefreshCw className="h-4 w-4" aria-hidden="true" />Status erneut prüfen</button>
      {can.withdraw && <button type="button" onClick={onWithdraw} data-testid="group-withdraw" className={V3_GHOST}><Undo2 className="h-4 w-4" aria-hidden="true" />Anfrage zurückziehen</button>}
    </div>
  </>);
}

const TITLES = { room: ["Gruppenraum, Regeln und Gruppenchat", "M15"], pending: ["Gruppenbeitritt ausstehend", "M71"] };

function Overview({ id }) {
  const { res, group: listed } = useGroup(id);
  const m = useMembership(listed);
  const [dialog, setDialog] = useState(null);
  const [message, setMessage] = useState("");
  const [dialogError, setDialogError] = useState("");
  if (res.error) return <ConnectionError onRetry={res.reload} />;
  if (res.data === null) return <Loading />;
  const g = m.group;
  if (!g) return <GroupNoAccess />;
  const blocked = m.outcome?.result === "unknown";
  const state = blocked ? "unknown" : membershipState(g);
  const isMember = state === "member";
  const paused = isGroupPaused(g);
  const can = groupActions(state, { blocked, paused });
  const msg = joinMessage(message);
  const view = isMember ? "room" : state === "join_pending" ? "pending" : "preview";
  const [title, mId] = TITLES[view] || [joinPolicy(g) === "approval" ? "Geschlossenen Kreis ansehen" : "Kreis ansehen", "M69"];
  const intro = isMember ? `Dein aufgenommener Kreis „${groupName(g)}“.` : view === "pending" ? "Deine Beitrittsanfrage wurde noch nicht entschieden." : "Vor der Aufnahme kannst du die öffentliche Beschreibung und Regeln lesen.";
  const closeDialog = () => { if (!m.busy) { setDialog(null); setDialogError(""); } };
  const act = async (kind) => {
    if (m.busy || (kind === "join" && msg.error)) return;
    setDialogError("");
    const o = await m.act(kind, kind === "join" && msg.value ? { message: msg.value } : undefined);
    if (!o) return;
    if (o.result === "failed") { setDialogError(o.message); return; }
    setDialog(null);
    if (kind === "join") setMessage("");
  };
  const note = m.outcome && m.outcome.result !== "success" && m.outcome.message;
  const body = view === "room" ? <Room g={g} onLeaveHref={groupHref(g, "/verlassen")} />
    : view === "pending" ? <Pending g={g} can={can} busy={m.busy} onWithdraw={() => setDialog("withdraw")} onRecheck={m.recheck} />
    : <Preview g={g} state={state} can={can} busy={m.busy} paused={paused} onJoin={() => setDialog("join")} onRecheck={m.recheck} />;
  return (
    <div data-m={mId} data-testid="group-detail">
      <V3Title title={title} intro={intro} testid="group-detail-title" />
      <V3Columns layout="app" aside={<GroupAside text={groupName(g)} />} main={<>
        <Notes paused={paused} note={note} unknown={blocked} />
        {view === "pending" ? body : <GroupFrame g={g} tab="uebersicht">{body}</GroupFrame>}
      </>} />
      <ConfirmDialog open={dialog === "join"} onOpenChange={(o) => !o && closeDialog()} testid="group-join-dialog" busy={m.busy || !!msg.error} title="Beitritt anfragen?" confirmLabel="Anfrage übermitteln" onConfirm={() => act("join")}
        description="Deine Anfrage wird an den Kreis übermittelt. Mitglied bist du erst, wenn das VISIBILIS-System es bestätigt. Ein Beitritt gibt deinen Access Pass nicht frei.">
        <V3TextArea id="group-join-message" label="Nachricht an den Kreis (freiwillig)" max={MESSAGE_MAX} value={message} onChange={(e) => setMessage(e.target.value)} error={msg.error} hint="Keine Gesundheits- oder Access-Pass-Angaben nötig." rows={3} />
        {dialogError && <ErrorNote testid="group-action-error">{dialogError}</ErrorNote>}
      </ConfirmDialog>
      <ConfirmDialog open={dialog === "withdraw"} onOpenChange={(o) => !o && closeDialog()} testid="group-withdraw-dialog" busy={m.busy} title="Beitrittsanfrage zurückziehen?" confirmLabel="Zurückziehen" cancelLabel="Anfrage behalten" onConfirm={() => act("withdraw")}
        description="Deine offene Anfrage an diesen Kreis wird zurückgezogen.">
        {dialogError && <ErrorNote testid="group-action-error">{dialogError}</ErrorNote>}
      </ConfirmDialog>
    </div>
  );
}

// M15, M69, M71, M120 on /kreise/:id; old ?reiter= links lead to the V3 sub-views.
export default function GroupDetail() {
  const { id } = useParams();
  const { search } = useLocation();
  const tab = legacyTab(search);
  if (tab) return <Navigate to={`/kreise/${encodeURIComponent(id)}/${tab}`} replace />;
  return <Overview key={id} id={id} />;
}
